import React from 'react';
import { View } from 'react-native';
import { Text } from '@shared/components/text';
import { Head } from '@shared/components/head';
import Button from '@shared/components/md-design-system/Button/Button';
import { Icon } from '@ant-design/react-native';
import { colorPalette } from '@shared/config/colorsConfig';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import { IPathwayDocument } from '../../types/Documents';
import { formatDocumentDate } from '../../utils/documentUtils';

interface PatientDocumentCardProps {
  document: IPathwayDocument;
  onView: () => void;
  onDelete: () => void;
  isViewing: boolean;
}

const StatusIndicator: React.FC<{ document: IPathwayDocument }> = ({ document }) => {
  const { t } = dynamicTranslations();
  const s = 'history.documents.patientSection';

  if (document.origin !== 'patient') {
    return (
      <View className="flex-row items-center gap-1">
        <Icon name="upload" size={12} color={colorPalette.gray[500]} />
        <Text className="text-xs text-gray-500">
          {t(`${s}.uploadedBy`, { name: document.authorName })}
        </Text>
      </View>
    );
  }

  const isViewed = !!document.professionalViewedAt;
  return (
    <View className="flex-row items-center gap-1">
      {isViewed ? (
        <View className="flex-row">
          <Icon name="check" size={12} color={colorPalette.gray[500]} />
          <View style={{ marginLeft: -7 }}>
            <Icon name="check" size={12} color={colorPalette.gray[500]} />
          </View>
        </View>
      ) : (
        <Icon name="clock-circle" size={12} color={colorPalette.gray[500]} />
      )}
      <Text className="text-xs text-gray-500">
        {isViewed ? t(`${s}.viewedByPhysician`) : t(`${s}.pendingView`)}
      </Text>
    </View>
  );
};

export const PatientDocumentCard: React.FC<PatientDocumentCardProps> = ({
  document,
  onView,
  onDelete,
  isViewing,
}) => {
  const { t } = dynamicTranslations();
  const s = 'history.documents.patientSection';

  const isPatientDoc = document.origin === 'patient';
  const canDelete = isPatientDoc && !document.professionalViewedAt;

  return (
    <View className="bg-white rounded-2xl mb-4 border border-gray-200 p-4">
      <View className="flex-row items-start justify-between mb-1">
        <Head type="h3" className="text-black flex-1 mr-3">
          {document.name || document.originalFileName}
        </Head>
        <StatusIndicator document={document} />
      </View>

      <Text className="text-base text-gray-500 mb-2">
        {t(`${s}.uploadedOn`, { date: formatDocumentDate(document.createdAt) })}
      </Text>
      <View className="flex-row gap-3 mt-1">
        <View className="self-start">
          <Button icon="eye" variant="primary" onPress={onView} disabled={isViewing}>
            {isViewing ? t(`${s}.viewing`) : t(`${s}.view`)}
          </Button>
        </View>

        {isPatientDoc && (
          <View className="self-start">
            <Button
              icon="delete"
              variant="danger-outline"
              onPress={onDelete}
              disabled={!canDelete}
            >
              {t(`${s}.delete`)}
            </Button>
          </View>
        )}
      </View>
    </View>
  );
};
